import { useState, useEffect } from 'react';
import { Trash2, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';
import { API, getAuthHeader } from './api';

export function QueriesManagement() {
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchQueries(); }, []);

  const fetchQueries = async () => {
    try { const response = await axios.get(`${API}/queries`, { headers: getAuthHeader() }); setQueries(response.data); }
    catch { /* silent */ }
    finally { setLoading(false); }
  };

  const markRead = async (id) => {
    try {
      await axios.put(`${API}/queries/${id}/read`, {}, { headers: getAuthHeader() });
      fetchQueries();
    } catch { toast.error('Failed to update'); }
  };

  const deleteQuery = async (id) => {
    if (!window.confirm('Delete this query?')) return;
    try {
      await axios.delete(`${API}/queries/${id}`, { headers: getAuthHeader() });
      toast.success('Query deleted');
      fetchQueries();
    } catch { toast.error('Failed to delete'); }
  };

  const unreadCount = queries.filter(q => !q.is_read).length;

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#231F20] font-['Bricolage_Grotesque']">Queries</h1>
          {unreadCount > 0 && <p className="text-sm text-[#C83041] mt-1">{unreadCount} unread</p>}
        </div>
      </div>
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Query</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {queries.map((query) => (
              <TableRow key={query.id} className={!query.is_read ? 'bg-blue-50/50' : ''}>
                <TableCell className="font-medium">{query.name}</TableCell>
                <TableCell>
                  <p className="text-sm">{query.email}</p>
                  <p className="text-xs text-gray-500">{query.phone}</p>
                </TableCell>
                <TableCell className="max-w-xs">
                  <p className="text-sm truncate" title={query.query}>{query.query}</p>
                </TableCell>
                <TableCell className="text-sm text-gray-500">{formatDate(query.submitted_at)}</TableCell>
                <TableCell>
                  <Badge className={query.is_read ? 'bg-gray-100 text-gray-600' : 'bg-blue-100 text-blue-700'}>
                    {query.is_read ? 'Read' : 'New'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    {!query.is_read && (
                      <Button size="sm" variant="ghost" onClick={() => markRead(query.id)} title="Mark as read">
                        <Eye className="w-4 h-4" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteQuery(query.id)} title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {queries.length === 0 && !loading && (
              <TableRow><TableCell colSpan={6} className="text-center text-[#58595B] py-8">No queries yet</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
